import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { aplicarTema, guardarTema, leerTemaGuardado, type Tema } from './tema'

export interface ContextoTema {
  tema: Tema
  cambiarTema: (tema: Tema) => void
}

export const contextoTema = createContext<ContextoTema | null>(null)

/**
 * Mantiene el tema elegido y lo refleja en el elemento raiz: el atributo
 * `data-theme` y la clase `tz-dark` de Trazabis DS (ver `tema.ts`).
 *
 * Con el tema en `sistema` escucha `prefers-color-scheme`: la clase `tz-dark`
 * lleva el tema ya resuelto, asi que si el sistema operativo cambia de claro a
 * oscuro hay que volver a ponerla. No re-renderiza nada; solo toca la clase.
 *
 * El estado inicial se lee de `localStorage` de forma sincrona, en el
 * inicializador de `useState`, para que el primer render ya sea el bueno.
 */
export function ProveedorTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(leerTemaGuardado)

  // El script de `index.html` ya ha puesto el atributo antes de la primera
  // pintura; esto lo mantiene en sintonia con el estado de React y cubre los
  // entornos donde ese script no corre, como las pruebas.
  useEffect(() => {
    aplicarTema(tema)
    if (tema !== 'sistema' || typeof window.matchMedia !== 'function') return
    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    const alCambiar = () => aplicarTema('sistema')
    consulta.addEventListener('change', alCambiar)
    return () => consulta.removeEventListener('change', alCambiar)
  }, [tema])

  const cambiarTema = useCallback((siguiente: Tema) => {
    setTema(siguiente)
    guardarTema(siguiente)
  }, [])

  const valor = useMemo(() => ({ tema, cambiarTema }), [tema, cambiarTema])

  return <contextoTema.Provider value={valor}>{children}</contextoTema.Provider>
}
